/**
 * Nomi, spiegazioni e conti della pagina BI (Davide 27/10/2026): «ogni dato
 * deve essere spiegato con una i» e «si deve capire in tutti i numeri come e'
 * stato calcolato». Le parole stanno qui, una volta sola: i componenti le
 * leggono, nessuno le riscrive.
 */

import type { MarketplaceSource } from "@/shared/ui/MarketplaceLogo";
import type { BiEconomia, BiGiorno } from "./biApi";

// ── Numeri ────────────────────────────────────────────────────────────────────

/** Il JSON porta i Decimal come numeri o stringhe: qui diventano numeri. */
export function n(value: number | string | null | undefined): number {
  const v = Number(value ?? 0);
  return Number.isFinite(v) ? v : 0;
}

/** In italiano «7400 €» non ha il punto e «35.450 €» si': in colonna sembrano
 *  due formati diversi. Il tipo di TS non conosce ancora «always». */
const SEMPRE_PUNTO = { useGrouping: "always" } as unknown as Intl.NumberFormatOptions;

export function eur(value: number | string | null | undefined, decimali = 0): string {
  if (value == null || !Number.isFinite(Number(value))) return "—";
  return Number(value).toLocaleString("it-IT", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: decimali,
    maximumFractionDigits: decimali,
    ...SEMPRE_PUNTO,
  });
}

export function perc(value: number | null | undefined, decimali = 1): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return `${value.toLocaleString("it-IT", {
    minimumFractionDigits: decimali,
    maximumFractionDigits: decimali,
  })}%`;
}

export function intero(value: number | string | null | undefined): string {
  return n(value).toLocaleString("it-IT", SEMPRE_PUNTO);
}

/** Margine % sul fatturato netto DEGLI STESSI ordini calcolabili. */
export function marginePerc(e: Pick<BiEconomia, "margin" | "net_revenue_computable">) {
  const base = n(e.net_revenue_computable);
  return base > 0 ? (n(e.margin) / base) * 100 : null;
}

export function calcolabili(e: BiEconomia): number {
  return n(e.certain_count) + n(e.estimate_count);
}

/** Quanti ordini del gruppo non sono un dato certo. */
export function incerti(e: BiEconomia): number {
  return n(e.estimate_count) + n(e.not_computable_count);
}

/** Variazione rispetto a prima, in %. Null se prima non c'era niente. */
export function variazione(adesso: number, prima: number | null | undefined): number | null {
  if (prima == null || prima === 0) return null;
  return ((adesso - prima) / Math.abs(prima)) * 100;
}

export function dataBreve(iso: string | null | undefined): string {
  if (!iso) return "—";
  const [y = "", m = "", d = ""] = iso.split("-");
  return `${d}/${m}/${y.slice(2)}`;
}

// ── Canali ────────────────────────────────────────────────────────────────────

const CANALI: Record<string, { nome: string; logo: MarketplaceSource; marchio?: string }> = {
  amazon_it: { nome: "Amazon IT", logo: "amazon" },
  amazon_es: { nome: "Amazon ES", logo: "amazon" },
  amazon_fr: { nome: "Amazon FR", logo: "amazon" },
  maisons_du_monde: { nome: "Maisons du Monde", logo: "mirakl", marchio: "Maisons du Monde" },
  leroy_merlin_it: { nome: "Leroy Merlin IT", logo: "mirakl", marchio: "Leroy Merlin" },
  leroy_merlin_es: { nome: "Leroy Merlin ES", logo: "mirakl", marchio: "Leroy Merlin" },
  leroy_merlin_fr: { nome: "Leroy Merlin FR", logo: "mirakl", marchio: "Leroy Merlin" },
  vente_unique_it: { nome: "Vente-Unique IT", logo: "mirakl", marchio: "Vente Unique" },
  vente_unique_es: { nome: "Vente-Unique ES", logo: "mirakl", marchio: "Vente Unique" },
  vente_unique_fr: { nome: "Vente-Unique FR", logo: "mirakl", marchio: "Vente Unique" },
  metro_it: { nome: "Metro IT", logo: "metro" },
  metro_es: { nome: "Metro ES", logo: "metro" },
  manomano_it: { nome: "ManoMano", logo: "manomano" },
  bricobravo: { nome: "BricoBravo", logo: "mirakl", marchio: "Brico Bravo" },
  temu_it: { nome: "Temu", logo: "temu" },
  shopify: { nome: "Kery (sito)", logo: "shopify" },
  woocommerce: { nome: "Bob Gardens (vecchio sito)", logo: "woocommerce" },
  ebay_it: { nome: "eBay", logo: "ebay" },
  generic: { nome: "Canale generico", logo: "privato" },
};

export function nomeCanale(id: string): string {
  return CANALI[id]?.nome ?? (id === "sconosciuto" ? "Canale non riconosciuto" : id);
}

export function logoCanale(id: string): { source: string; label?: string } {
  const c = CANALI[id];
  return c ? { source: c.logo, label: c.marchio ?? c.nome } : { source: id };
}

/** Commissione del canale: come la conta OM, detta in parole. */
export function regolaCommissione(id: string): string {
  if (id.startsWith("amazon")) return "15,45% fino a 200 € di prezzo, 10,30% sulla parte sopra";
  if (id === "temu_it") return "0%: Temu non trattiene commissione";
  if (id === "shopify") return "la fee vera di Shopify Payments, 2% se il pagamento e' PayPal";
  if (id === "manomano_it") return "15% sul prezzo senza IVA";
  if (id.startsWith("leroy_merlin")) return "12% sul prezzo con IVA";
  if (id === "woocommerce" || id === "generic") return "nessuna";
  return "15% sul prezzo con IVA, o quella vera imparata da Analisi Marketplace";
}

// ── Fornitori ─────────────────────────────────────────────────────────────────

const FORNITORI: Record<string, string> = {
  itamoby: "Itamoby",
  ityhome: "Ityhome",
  allservice: "AllService",
  cosma: "Cosma",
  bscom: "Magazzino BS COM",
  bizzotto: "Bizzotto",
  web_furniture: "Web Furniture",
  mauro_ferretti: "Mauro Ferretti",
  aurora: "Aurora Bedding",
  wonderland: "Wonderland",
  ugoflex: "Ugoflex",
  you_self: "You Self",
  divina: "Divina",
  oyat: "Oyat",
  metalfar: "Metal F.A.R.",
  unknown: "Non identificato",
};

export function nomeFornitore(kind: string): string {
  return FORNITORI[kind] ?? kind;
}

export const SPIEGA_FORNITORE_IGNOTO =
  "OM non trova il fornitore in nessuna delle tre fonti: codice dell'ordine fornitore, fattura d'acquisto, catalogo (PIM). Succede quando le righe dell'ordine sono di fornitori diversi o un prodotto non e' nel catalogo. Non lo indoviniamo: un fornitore indovinato e' un dato falso.";

/** Da dove viene il fornitore di un gruppo: quanti dalla fattura, quanti dal catalogo. */
export function spiegaFonteFornitore(
  nome: string,
  ordini: number,
  daFattura: number,
  daCatalogo: number,
): { titolo: string; testo: string } | undefined {
  if (!daFattura && !daCatalogo) return undefined;
  const parti = [];
  if (daFattura) parti.push(`${daFattura} dalla fattura d'acquisto`);
  if (daCatalogo)
    parti.push(`${daCatalogo} dal catalogo (PIM): tutte le righe dell'ordine sono di ${nome}`);
  const resto = ordini - daFattura - daCatalogo;
  if (resto > 0) parti.push(`${resto} dal codice dell'ordine fornitore`);
  return { titolo: nome, testo: `Come lo sappiamo: ${parti.join(", ")}.` };
}

// ── Motivi delle stime ────────────────────────────────────────────────────────

/** Il motivo salvato dal calcolo del margine, detto semplice. */
const MOTIVI: Record<string, { titolo: string; spiega: string; grave?: boolean }> = {
  "commissione di listino": {
    titolo: "Commissione standard del canale",
    spiega:
      "Per questo prodotto non c'e' la commissione vera vista sulle vendite: OM usa la percentuale standard del canale.",
  },
  "commissione sconosciuta": {
    titolo: "Commissione sconosciuta",
    spiega: "Il canale non ha una commissione nota. Il margine non si calcola.",
    grave: true,
  },
  "spedizione stimata": {
    titolo: "Spedizione stimata",
    spiega:
      "Il costo di spedizione viene da una stima di Analisi Marketplace: la fattura del corriere (Bracchi o BRT) di questo ordine non c'è ancora.",
  },
  "spedizione sconosciuta": {
    titolo: "Spedizione sconosciuta",
    spiega: "Nessun costo di spedizione per questo prodotto. Il margine non si calcola.",
    grave: true,
  },
  "logistica AllService sconosciuta": {
    titolo: "Logistica AllService mancante",
    spiega:
      "Non abbiamo le tariffe di picking ed etichetta dal file AllService: quei pochi euro non sono nel conto.",
    grave: false,
  },
  "spedizione estero sconosciuta": {
    titolo: "Spedizione estero sconosciuta",
    spiega:
      "Ordine Kery fuori dall'Italia: il costo Bracchi per l'estero non lo conosciamo ancora. Il margine non si calcola.",
    grave: true,
  },
  "spedizione per pezzo (piu' pezzi)": {
    titolo: "Spedizione moltiplicata per i pezzi",
    spiega:
      "Piu' pezzi nello stesso ordine: la spedizione e' contata una volta per pezzo. Se viaggiano insieme costa meno.",
  },
  "spedizione di gruppo (AM)": {
    titolo: "Spedizione di gruppo",
    spiega:
      "Piu' pezzi dello stesso fornitore viaggiano insieme: la spedizione e' la tariffa sul peso totale calcolata da Analisi Marketplace, come al checkout Kery.",
  },
  "spedizione aggregata per SKU (ordine multi-articolo)": {
    titolo: "Spedizione sommata per articolo",
    spiega: "Ordine con piu' articoli: la spedizione e' la somma di quella di ogni articolo.",
  },
  "costo prodotto da catalogo (conferma fornitore mancante)": {
    titolo: "Costo prodotto da listino",
    spiega:
      "Manca la conferma del fornitore con il prezzo pagato: OM usa il costo di listino del PIM.",
  },
  "costo prodotto sconosciuto": {
    titolo: "Costo prodotto sconosciuto",
    spiega: "Nessun costo per questo prodotto. Il margine non si calcola.",
    grave: true,
  },
  "costo prodotto parziale (righe senza costo)": {
    titolo: "Costo prodotto incompleto",
    spiega: "In un ordine con piu' articoli almeno uno non ha costo. Il margine non si calcola.",
    grave: true,
  },
  "costo inserito a mano, spedizione compresa": {
    titolo: "Costo scritto a mano",
    spiega: "Il costo l'ha scritto un operatore e comprende gia' la spedizione.",
  },
  "marketplace non riconosciuto": {
    titolo: "Canale non riconosciuto",
    spiega:
      "OM non sa di che canale e' l'ordine: niente commissione, niente IVA. Il margine non si calcola.",
    grave: true,
  },
  "motivo non ancora salvato": {
    titolo: "Motivo non ancora salvato",
    spiega:
      "Margine calcolato prima del 28/10, quando OM non salvava il motivo. Si completa da solo al prossimo ricalcolo.",
  },
};

export function motivo(chiave: string): { titolo: string; spiega: string; grave: boolean } {
  const m = MOTIVI[chiave];
  if (m) return { ...m, grave: m.grave ?? false };
  if (chiave.startsWith("IVA del paese sconosciuta")) {
    return {
      titolo: "Paese o IVA sconosciuti",
      spiega: `Senza paese non si sa quanta IVA togliere (${chiave}). Il margine non si calcola.`,
      grave: true,
    };
  }
  if (chiave.startsWith("IVA Shopify")) {
    return {
      titolo: "IVA Shopify diversa dal previsto",
      spiega: `L'IVA applicata da Shopify non e' quella del paese di consegna (${chiave}). Si usa quella di Shopify.`,
      grave: false,
    };
  }
  if (chiave.startsWith("stima salvata prima del 28/10")) {
    return {
      titolo: "Stima vecchia, motivo perso",
      spiega:
        "Il margine e' chiuso da prima del 28/10 e oggi il calcolo non trova piu' il motivo della stima.",
      grave: false,
    };
  }
  return { titolo: chiave, spiega: "Motivo scritto dal calcolo del margine.", grave: false };
}

// ── Andamento ─────────────────────────────────────────────────────────────────

export type Grana = "giorno" | "settimana" | "mese";

function chiaveGrana(iso: string, grana: Grana): string {
  if (grana === "giorno") return iso;
  const d = new Date(`${iso}T00:00:00`);
  if (grana === "mese") return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
  const lunedi = new Date(d);
  lunedi.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  const m = String(lunedi.getMonth() + 1).padStart(2, "0");
  const g = String(lunedi.getDate()).padStart(2, "0");
  return `${lunedi.getFullYear()}-${m}-${g}`;
}

const SOMMABILI = [
  "orders",
  "net_revenue",
  "net_missing_count",
  "certain_count",
  "estimate_count",
  "not_computable_count",
  "net_revenue_computable",
  "commission",
  "shipping",
  "packaging",
  "product_cost",
  "margin",
  "loss_count",
  "loss_amount",
] as const;

/** Giorni raggruppati in settimane (dal lunedi') o mesi. */
export function raggruppa(giorni: BiGiorno[], grana: Grana): BiGiorno[] {
  const secchi = new Map<string, BiGiorno>();
  for (const g of giorni) {
    const k = chiaveGrana(g.day, grana);
    const s = secchi.get(k);
    if (!s) {
      const nuovo = { day: k } as BiGiorno;
      for (const c of SOMMABILI) nuovo[c] = n(g[c]);
      secchi.set(k, nuovo);
    } else {
      for (const c of SOMMABILI) s[c] = n(s[c]) + n(g[c]);
    }
  }
  return [...secchi.values()].sort((a, b) => a.day.localeCompare(b.day));
}
